/**
 * Lo que se le factura al cliente en un mes: costo de operar más margen.
 *
 * Los totales llegan en `null` (no en cero) cuando falta un insumo, y `avisos`
 * dice cuál. La pantalla tiene que respetar esa distinción: un total
 * incompleto pintado como número se lee como un total completo más barato.
 *
 * `subtotal_mxn` es SÓLO el servidor. El consumo de IA se muestra aparte
 * porque la llave la designó el cliente y ese gasto ya salió de su bolsa.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";

export interface BillingIaBlock {
  llamadas: number;
  tokens: number;
  costo_usd: number;
  costo_mxn: number | null;
  /** La llave es del cliente: lo paga él directo, no se le factura otra vez. */
  facturable: boolean;
  alcance: string;
}

export interface BillingInfraBlock {
  medido: boolean;
  salida_bytes: number | null;
  incluido_bytes: number | null;
  excedente_bytes: number | null;
  renta: number | null;
  excedente: number | null;
  moneda: string | null;
  costo_moneda: number | null;
  costo_mxn: number | null;
  alcance: string;
}

export interface BillingSummary {
  periodo: string;
  desde: string;
  hasta: string;
  moneda: string;
  ia: BillingIaBlock;
  infra: BillingInfraBlock;
  margen_pct: number | null;
  subtotal_mxn: number | null;
  margen_mxn: number | null;
  total_mxn: number | null;
  /** Fecha de facturación pedida; null = cierre del periodo. */
  fecha_factura?: string | null;
  referencia_tipo_cambio?: string | null;
  /** Con qué tipo de cambio se convirtió el servidor. */
  tipo_cambio?: BillingTipoCambio | null;
  avisos: string[];
}

export interface BillingTipoCambio {
  par: string;
  rate: number;
  /** "banxico" o "manual". */
  fuente: string;
  vigente_desde: string | null;
  /** Fecha del FIX de Banxico (YYYY-MM-DD); null si fue manual. */
  fecha_fix: string | null;
  nota: string | null;
}

export interface BillingMargin {
  id: string;
  margin_pct: number;
  effective_from: string;
  effective_until: string | null;
  updated_by: string | null;
  updated_by_username: string | null;
  updated_at: string;
  notes: string | null;
  is_current: boolean;
}

export interface BillingMarginList {
  current: BillingMargin | null;
  history: BillingMargin[];
}

export function useBillingSummary(periodo: string | null, fechaFactura: string | null = null) {
  const params = new URLSearchParams();
  if (periodo) params.set("period", periodo);
  if (fechaFactura) params.set("fecha_factura", fechaFactura);
  const qs = params.toString() ? `?${params.toString()}` : "";
  return useQuery({
    queryKey: ["billing", "summary", periodo, fechaFactura],
    queryFn: () => api<BillingSummary>(`/api/v1/admin/billing/summary${qs}`),
  });
}

export function useBillingMargin() {
  return useQuery({
    queryKey: ["billing", "margin"],
    queryFn: () => api<BillingMarginList>("/api/v1/admin/billing/margin"),
  });
}

export function useSetBillingMargin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { margin_pct: number; notes?: string }) =>
      api<BillingMargin>("/api/v1/admin/billing/margin", { method: "PUT", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["billing"] });
    },
  });
}
